import { Deserializable } from './deserializable';
import { Type } from '@angular/core';
import { WebSocketReceiveMessage } from '../web-sockets/messages/receive/web-socket-receive-message';
import { WebSocketMessageContextConnectionManager } from '../web-sockets/contexts/web-socket-message-context-connection-manager';
import { WebSocketConnectionEstablished } from '../web-sockets/messages/receive/web-socket-connection-established';
import { WebSocketPong } from '../web-sockets/messages/receive/web-socket-pong';
import { WebSocketHealthCheck } from '../web-sockets/messages/receive/web-socket-health-check';
import { WebSocketReceiveData } from '../web-sockets/messages/receive/web-socket-receive-data';
import { WebSocketMessageContextResponse } from '../web-sockets/contexts/web-socket-message-context-response';
import { WebSocketReceiveUpdate } from '../web-sockets/messages/receive/web-socket-receive-update';
import { WebSocketBudBotResponse } from '../web-sockets/messages/receive/web-socket-bud-bot-response';
import { WebSocketBudBotConversationResponse } from '../web-sockets/messages/receive/web-socket-bud-bot-conversation-response';
import { WebSocketMessageContextEvent } from '../web-sockets/contexts/web-socket-message-context-event';
import { WebSocketMessageContextDataUpdate } from '../web-sockets/contexts/web-socket-message-context-data-update';
import { WebSocketVariantLookupResponse } from '../web-sockets/messages/receive/web-socket-variant-lookup-response';
import { WebSocketAsyncDeleteStatusUpdate } from '../web-sockets/messages/receive/web-socket-async-delete-status-update';
import { WebSocketMessageType } from '../web-sockets/web-socket-message-type';
import { WebSocketReceiveError } from '../web-sockets/messages/receive/web-socket-receive-error';
import { WebSocketReceiveErrorWithNoRestfulFallback } from '../web-sockets/messages/receive/web-socket-receive-error-with-no-restful-fallback';
import { WebSocketAssetUploaded } from '../web-sockets/messages/receive/web-socket-asset-uploaded';
import { WebSocketAssetDeleted } from '../web-sockets/messages/receive/web-socket-asset-deleted';
import { WebSocketBrandsDeleted } from '../web-sockets/messages/receive/web-socket-brands-deleted';
import { WebSocketBrandAttributeGroupsDeleted } from '../web-sockets/messages/receive/web-socket-brand-attribute-groups-deleted';
import { WebSocketBrandProductMapsDeleted } from '../web-sockets/messages/receive/web-socket-brand-product-maps-deleted';
import { WebSocketReceiveAssetUpdated } from '../web-sockets/messages/receive/web-socket-receive-asset-updated';
import { WebSocketAssetTranscodeStatusUpdate } from '../web-sockets/messages/receive/web-socket-asset-transcode-status-update';
import { WebSocketSyncJobUpdate } from '../web-sockets/messages/receive/web-socket-sync-job-update';

export class DeserializeWebSocketMessageHelper {
  /**
   * If a message has explicit data types coming back, then the WebSocketMessage object needs to be extended and
   * the specific override for the payload property needs to be implemented (with its own deserialization logic).
   */
  static getPolymorphicMessageObject<T extends Deserializable>(message: any): T {
    const createPolymorphicObject = (ObjectType: Type<any>): T => {
      return Object.assign(Object.create(ObjectType.prototype), message);
    };
    if (message?.messageType === WebSocketMessageType.ErrorWithNoRestfulFallback) {
      return createPolymorphicObject(WebSocketReceiveErrorWithNoRestfulFallback);
    }
    if (message?.messageType === WebSocketMessageType.Error) {
      return createPolymorphicObject(WebSocketReceiveError);
    }
    switch (message?.messageContext) {
      case WebSocketMessageContextConnectionManager.ConnectionEstablished:
        return createPolymorphicObject(WebSocketConnectionEstablished);
      case WebSocketMessageContextConnectionManager.Pong:
        return createPolymorphicObject(WebSocketPong);
      case WebSocketMessageContextConnectionManager.HealthCheck:
        return createPolymorphicObject(WebSocketHealthCheck);
      case WebSocketMessageContextDataUpdate.AssetUpdated:
        return createPolymorphicObject(WebSocketReceiveAssetUpdated);
      case WebSocketMessageContextEvent.AssetTranscodeStatusUpdate:
        return createPolymorphicObject(WebSocketAssetTranscodeStatusUpdate);
      case WebSocketMessageContextEvent.AssetUploaded:
        return createPolymorphicObject(WebSocketAssetUploaded);
      case WebSocketMessageContextEvent.AssetDeleted:
        return createPolymorphicObject(WebSocketAssetDeleted);
      case WebSocketMessageContextEvent.BrandsDeleted:
        return createPolymorphicObject(WebSocketBrandsDeleted);
      case WebSocketMessageContextEvent.BrandAttributeGroupsDeleted:
        return createPolymorphicObject(WebSocketBrandAttributeGroupsDeleted);
      case WebSocketMessageContextEvent.BrandProductMapsDeleted:
        return createPolymorphicObject(WebSocketBrandProductMapsDeleted);
      case WebSocketMessageContextEvent.SyncJobUpdate:
        return createPolymorphicObject(WebSocketSyncJobUpdate);
      case WebSocketMessageContextResponse.BudBot:
        return createPolymorphicObject(WebSocketBudBotResponse);
      case WebSocketMessageContextResponse.BudBotConversation:
        return createPolymorphicObject(WebSocketBudBotConversationResponse);
      case WebSocketMessageContextEvent.VariantLookupResponse:
        return createPolymorphicObject(WebSocketVariantLookupResponse);
      case WebSocketMessageContextEvent.AsyncDeleteStatusUpdate:
        return createPolymorphicObject(WebSocketAsyncDeleteStatusUpdate);
      case WebSocketMessageContextResponse.RequestResponse:
        if (message?.payload?.messageContext?.includes('Update')) {
          return createPolymorphicObject(WebSocketReceiveUpdate);
        }
        return createPolymorphicObject(WebSocketReceiveData);
    }
    return createPolymorphicObject(WebSocketReceiveMessage);
  }
}
